import { useCopilotAction } from '@copilotkit/react-core';

import { TrackableList, TrackableObjectType } from 'types/trackables';

import { useAPI } from 'hooks/api/useAPI';

// Helper to safely parse JSON or return the value if already parsed
function safeJsonParse<T>(value: string | T | undefined): T | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    // If it's a string that's not valid JSON, treat it as a single-item array
    return [value] as unknown as T;
  }
}

export function useListWatchlistsTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listWatchlists',
    description:
      'List watchlists (trackable lists) in the system. Watchlists contain items like faces, vehicles, or custom objects that can be tracked. Use this to find watchlist IDs for querying watchlist items.',
    parameters: [
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of watchlist IDs to fetch',
        required: false,
      },
      {
        name: 'search',
        type: 'string',
        description: 'Search term to filter watchlists by name',
        required: false,
      },
      {
        name: 'objectTypes',
        type: 'string',
        description:
          'JSON array of object types to filter by: ["face", "vehicle", "custom"]',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      ids?: string | string[];
      search?: string;
      objectTypes?: string | TrackableObjectType[];
      limit?: number;
    }) => {
      try {
        const ids = safeJsonParse<string[]>(args.ids);
        const objectTypes = safeJsonParse<TrackableObjectType[]>(args.objectTypes);

        const { data } = await client.get(`/apps/${applicationID}/trackables`, {
          params: {
            pagination: 'offset',
            page: 1,
            limit: args.limit || 50,
            trackable_list_ids: ids,
            trackable_list_names: args.search ? [args.search] : undefined,
            object_types: objectTypes,
          },
        });

        const watchlists = data?.data || [];

        if (watchlists.length === 0) {
          return {
            message: 'No watchlists found matching your criteria.',
            watchlists: [],
          };
        }

        return {
          message: `Found ${watchlists.length} watchlist(s)`,
          watchlists: watchlists.map((watchlist: TrackableList) => ({
            id: watchlist.id,
            name: watchlist.name,
            description: watchlist.description,
            objectType: watchlist.object_type,
            createdAt: watchlist.created_at,
            updatedAt: watchlist.updated_at,
          })),
        };
      } catch (error) {
        return {
          message: 'Failed to load watchlists',
          error: error instanceof Error ? error.message : 'Unknown error',
          watchlists: [],
        };
      }
    },
  });
}
